/**
 * The open incidents: every defect the scanner, the brain or the watch has seen
 * and not yet seen gone, and whether an alert call may use it right now.
 *
 * Rows rather than a table, like the autopilot's run log. The call column
 * never says just "no": it names the reason (too old, waiting for a second
 * check, already called), because that is what decides whether the operator
 * waits or moves on.
 *
 * Its own card, under the watch's, since the list became the Rikked tab's
 * content. It used to be the bottom of the watch card, a guest folded to eight
 * rows under a "show all" button; the fold went with the move, as the server
 * already caps the list (`OPEN_INCIDENTS_LIMIT`) and says so under it. The
 * subtitle names the three sources, because with the watch off - as it is in
 * production - every row here came from a scan or a brain run.
 */
import { Badge } from "@wa/ui";
import { formatWhen } from "../../../../lib/siteScan";
import {
  OPEN_INCIDENTS_LIMIT,
  classLabelKey,
  classTone,
  findingLabelKey,
  fullWhen,
  incidentCallLabel,
  type IncidentView,
} from "../../../../lib/siteWatch";
import { listCard } from "../../../ui";
import type { T } from "../../site-scanner/types";

/** Defect, website, first seen, confirmed, call. */
const COLS = "1.4fr 1.3fr .8fr .8fr 1fr";

function IncidentRow({ incident, dates, t }: { incident: IncidentView; dates: string; t: T }) {
  const findingKey = findingLabelKey(incident.findingId);
  const classKey = classLabelKey(incident.class);
  const call = incidentCallLabel(incident);

  return (
    <div className="crm-list-row" style={{ gridTemplateColumns: COLS }}>
      <span className="flex min-w-0 flex-col items-start gap-1 pl-3">
        <span className="crm-trunc max-w-full text-[13px] font-[550]">
          {findingKey ? t(findingKey) : incident.findingId}
        </span>
        <Badge variant={classTone(incident.class)}>{classKey ? t(classKey) : incident.class}</Badge>
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="crm-trunc text-[13px]">{incident.domain}</span>
        <span className="crm-mut crm-trunc text-[12px]">
          {incident.companyName ?? t("leads.watch.incidents.noCompany")}
        </span>
      </span>
      <span className="crm-tnum crm-trunc text-[12.5px]" title={fullWhen(incident.firstSeenAt, dates)}>
        {formatWhen(incident.firstSeenAt, dates)}
      </span>
      <span className="crm-tnum crm-trunc text-[12.5px]" title={fullWhen(incident.confirmedAt, dates)}>
        {incident.confirmedAt ? (
          formatWhen(incident.confirmedAt, dates)
        ) : (
          <span className="crm-mut">{t("leads.watch.incidents.notConfirmed")}</span>
        )}
      </span>
      <span className="min-w-0">
        <Badge variant={call.tone} className="max-w-full">
          <span className="crm-trunc">{call.key ? t(call.key) : (call.raw ?? "-")}</span>
        </Badge>
      </span>
    </div>
  );
}

export function IncidentList({
  incidents,
  failed,
  dates,
  t,
}: {
  incidents: IncidentView[] | null;
  failed: boolean;
  /** Intl locale for the date columns. */
  dates: string;
  t: T;
}) {
  const head = (
    <div className="crm-card-head">
      <div className="crm-card-col">
        <h2 className="crm-card-t">{t("leads.watch.incidents.title")}</h2>
        <div className="crm-card-s">{t("leads.watch.incidents.sub")}</div>
      </div>
    </div>
  );

  if (!incidents) {
    if (!failed) return null;
    return (
      <section className="crm-card">
        {head}
        <p className="crm-sub px-[26px] pb-5 pt-2">{t("leads.watch.incidents.loadFailed")}</p>
      </section>
    );
  }

  if (!incidents.length) {
    return (
      <section className="crm-card">
        {head}
        <p className="crm-sub px-[26px] pb-5 pt-2">{t("leads.watch.incidents.empty")}</p>
      </section>
    );
  }

  // The server sends at most this many; a full page means there may be more.
  const capped = incidents.length >= OPEN_INCIDENTS_LIMIT;

  return (
    <section className="crm-card pb-3">
      {head}
      {failed ? <p className="crm-sub px-[26px] pt-2">{t("leads.watch.incidents.loadFailed")}</p> : null}
      {/* `crm-list-scroll`: below the list's floor the rows scroll inside the
          card, so a swipe on a phone does not drag the whole card sideways. */}
      <div className="crm-list-scroll pt-2">
        <div className="crm-list-head" style={{ ...listCard(COLS), gridTemplateColumns: COLS, paddingTop: 0 }}>
          <span>{t("leads.watch.incidents.col.finding")}</span>
          <span>{t("leads.watch.incidents.col.site")}</span>
          <span>{t("leads.watch.incidents.col.firstSeen")}</span>
          <span>{t("leads.watch.incidents.col.confirmed")}</span>
          <span>{t("leads.watch.incidents.col.call")}</span>
        </div>
        <div className="crm-rows pt-0" style={listCard(COLS)}>
          {incidents.map((incident) => (
            <IncidentRow key={incident.id} incident={incident} dates={dates} t={t} />
          ))}
        </div>
      </div>

      {capped ? (
        <p className="crm-sub px-[26px] pt-2 text-[11.5px]">
          {t("leads.watch.incidents.limit", { limit: OPEN_INCIDENTS_LIMIT })}
        </p>
      ) : null}
    </section>
  );
}
